import React, { useMemo, useState } from 'react';
import {
  Box,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import InvoiceCategoryTabs from '../../../components/Billing/Invoice/InvoiceCategoryTabs';
import SearchBar from '../../../components/Billing/Invoice/InvoiceSearchBar';
import InvoiceFilter, { InvoiceFilterValues } from '../../../components/Billing/Invoice/InvoiceFilter';
import { ReactComponent as FunnelIcon } from '../../../assets/SVG/Filter Icon Box.svg';
import RefundRequestList from './RefundRequestList';
import RefundedList from './RefundedList';
import { usePatientContext } from '../../../components/SubPatinetProvider';
import HeaderProfile from '../../../components/header_Profile/header_Profile.index';
import { useAppQuery } from '../../../services/appQuery';
import { getRefundStats, RefundStats } from './Refund.helper';
import { getRequest } from '../../../services/httpService';
import { useTranslation } from 'react-i18next';

type RefundTabKey = 'REFUND_REQUEST' | 'REFUND_LOGS';

const PatientRefund: React.FC = () => {
  const [activeTab, setActiveTab] = useState<RefundTabKey>('REFUND_REQUEST');
  const [search, setSearch] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<InvoiceFilterValues>({ fromDate: '', toDate: '', billType: '' });
  const { patientId } = usePatientContext();
  const { t: tBilling } = useTranslation('billing_config');

  const { data: refundStats } = useAppQuery<RefundStats>(
    ['RefundStats', patientId],
    () => {
      const baseUrl = getRefundStats();
      const url = patientId ? `${baseUrl}?patient_id=${patientId}` : baseUrl;
      return getRequest<RefundStats>(url);
    },
    { staleTime: 0, gcTime: 0, enabled: true, refetchOnMount: true }
  );
    

  const categories = useMemo(
    () => [
      { label: tBilling('Refund.refund_request', 'Refund Request'), name: 'REFUND_REQUEST', count: refundStats?.refund_requested || 0 },
      { label: tBilling('RefundDetails.tabs.refundLogs', 'Refund Logs'), name: 'REFUND_LOGS', count: refundStats?.refunded || 0 },
    ],
    [refundStats, tBilling]
  );


  const renderTabContent = () => {
    switch (activeTab) {
    case 'REFUND_REQUEST':
      return <RefundRequestList patientId={patientId} search={search} filters={filters} />;
    case 'REFUND_LOGS':
      return <RefundedList patientId={patientId} search={search} filters={filters} />;
    default:
      return null;
    }
  };

  return (
    <>
      <HeaderProfile />
      <Box sx={{ px: 3, py: 2 }} data-testid="patient-refund-container">
        {/* Header: Tabs left, filter + search right (same pattern as Advance) */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }} data-testid="patient-refund-header-section">
          <InvoiceCategoryTabs
            activeCategory={activeTab}
            categories={categories}
            onCategoryChange={(cat) => setActiveTab(cat.name as RefundTabKey)} />

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title={tBilling('invoice.filter.title', 'Filters')} arrow>
              <IconButton aria-label={tBilling('Refund.common.openFiltersAria', 'Open filters')} onClick={() => setIsFilterOpen(true)} sx={{ width: 40, height: 40, p: 0 }}>
                <FunnelIcon style={{ width: 40, height: 40 }} />
              </IconButton>
            </Tooltip>
            {!patientId && (
              <SearchBar
                height={40}
                value={search}
                onChange={setSearch}
                placeholder={tBilling('Refund.search_by_patient_name', 'Search By Patient Name')}
                minWidth={420} />

            )}
          </Stack>
        </Stack>
        {/* Content area for the active tab */}
        {renderTabContent()}
        {/* Shared filter modal */}
        <InvoiceFilter
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={(vals) => { setFilters(vals); setIsFilterOpen(false); } }
          showBillType={false}
          initialValues={filters} />

      </Box></>
  );
};

export default PatientRefund;
